import { parseISO } from "date-fns";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Col, Row } from "react-bootstrap";
import { Container, useToast } from "../../components";
import { Location, OrderStatus, Space } from "../../data";
import {
  getMembershipInfoFromLocation,
  getMembershipOfferingLocationFromLocationId,
  LocatableType,
  PartnerOffering,
} from "../../data/partner_offering";
import { useI18n } from "../../lib/i18n/i18n_provider";
import {
  invalidateOrderCache,
  invalidateOrderLineItemCache,
  Order,
  OrderableType,
  OrderItem,
  OrderItemMultiPass,
  useCancelOrder,
  useMembershipPoliciesQuery,
  useOrderInviteQuery,
} from "../../offering/api";
import { getMainOrderItem } from "../../offering/order";
import { PartnerConfig } from "../../offering/partner_config";
import { Spacer } from "../../offering/spacer";
import { Text } from "../../offering/text";
import { MembershipOrderSummary, OrderSummary } from "../components/quote_view";
import {
  OrderBillingDetails,
  OrderBillingDetailsMembership,
  OrderGuestDetails,
  OrderGuestDetailsMembership,
  OrderPaymentMethod,
  OrderPaymentMethodMembership,
  OrderTax,
} from "../../offering/template_offering_confirm_order_base";
import {
  FooterV2,
  IntelligentOfficeRichardPromo,
  OfferingHeader as OfferingHeaderV2,
  PromoCodeBox,
  SendInvites,
} from "../components";
import { TalkWithUsPrompt } from "../components/talk_with_us_prompt";
import Modal from "react-bootstrap/esm/Modal";
import Button from "react-bootstrap/esm/Button";
import { CancellationPolicy } from "../components/cancellation_policy";
import { useMediaQuery, useQueryString } from "../../hooks";
import classNames from "classnames";
import { useCheckPurchaseStatus } from "../../data/api";
import { PortalOnboarding } from "../../offering/portal_onboarding";
import { useIntelligentOfficePromoFeatureFlag } from "@/core/splitio";
import { offeringConfig } from "@/offering/offering_config";
import { OfferingHeader } from "@/offering/offering_header";

interface TemplateOfferingConfirmOrderBaseProps {
  partnerConfig: PartnerConfig;
  confirmationMessage?: (
    order: Order,
    orderItem: OrderItem | undefined
  ) => React.ReactNode;
  space: Space;
  location: Location;
  offeringName: string;
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  order: Order;
  includeQuantity?: boolean;
  numberOfInvites?: number;
}

const CANCEL_REASON =
  "Cancelled by the customer from the order confirmation screen.";

export function TemplateOfferingConfirmOrderBaseV2(
  props: TemplateOfferingConfirmOrderBaseProps
) {
  const {
    order,
    space,
    location,
    offeringName,
    partnerOffering,
    partnerOfferingID,
    partnerConfig,
    numberOfInvites,
    confirmationMessage,
  } = props;
  const {
    hubspotForm,
    favicon,
    showTalkWithUsPrompt = true,
    talkWithUsMessage,
    localeOptions,
    hubspotForms,
    instagramLink,
  } = partnerConfig;
  const { locale, fbt } = useI18n();

  const [viewCancelOrderConfirmation, setViewCancelOrderConfirmation] =
    useState<boolean>(false);
  const [disableConfirmationButton, setDisableConfirmationButton] =
    useState<boolean>(false);

  const cancelOrder = useCancelOrder();
  const toast = useToast();
  const { data: invites, refetch: refetchInvites } = useOrderInviteQuery(
    order.id
  );
  const mainOrderItem = getMainOrderItem(order);

  const handleOpenDialog = useCallback(() => {
    setViewCancelOrderConfirmation(true);
  }, []);

  const orderItemBooking = order.orderItems.find(
    (orderItem) => orderItem.type === OrderableType.Booking
  );

  const orderItemAddon = order.orderItems.find(
    (orderItem) => orderItem.type === OrderableType.AddOn
  ) as OrderItemMultiPass;

  const orderItemID = orderItemBooking?.orderItemID
    ? orderItemBooking?.orderItemID
    : orderItemAddon?.orderItemID;

  const cancelledAt = order.cancelledAt
    ? fbt.formatDateByPattern(parseISO(order.cancelledAt), " MMM do, yyyy")
    : "";

  const renderOrderTitle = () => {
    if (order.status === OrderStatus.Canceled) {
      return (
        <Fragment>
          <h1>{fbt("Order cancelled")}</h1>
          <Spacer size={9} />
          <p>
            <b>{fbt("Cancelled and refunded ")}</b>
            {fbt("on " + fbt.param("cancelledAt", cancelledAt) + ".")}
          </p>
        </Fragment>
      );
    } else if (order.status === OrderStatus.Rejected) {
      return (
        <Fragment>
          <h1>{fbt("Order rejected")}</h1>
          <Spacer size={9} />
          <p>
            <b>{fbt("Rejected and refunded")}</b>
          </p>
        </Fragment>
      );
    } else if (order.status === OrderStatus.DisputePending) {
      return (
        <Fragment>
          <h1>{fbt("Order is being disputed")}</h1>
          <Spacer size={9} />
        </Fragment>
      );
    } else if (order.status === OrderStatus.DisputeLost) {
      return (
        <Fragment>
          <h1>{fbt("Order was disputed")}</h1>
          <Spacer size={9} />
        </Fragment>
      );
    } else {
      return (
        <Fragment>
          <h1>
            {fbt(
              space?.spaceType === "event_space"
                ? "Booking Submitted!"
                : "Confirmed!"
            )}
          </h1>
          <Spacer size={9} />
          {confirmationMessage ? (
            confirmationMessage(order, mainOrderItem)
          ) : (
            <p>
              {fbt(
                "Thank you for booking with us. A confirmation email has been sent to " +
                  fbt.param("email", order.email) +
                  ". We are looking forward to seeing you soon."
              )}
            </p>
          )}
        </Fragment>
      );
    }
  };

  const handleCancelOrder = useCallback(
    async (orderID: string) => {
      try {
        setDisableConfirmationButton(true);
        const response = await cancelOrder(orderID, CANCEL_REASON);
        if (response.status === "failed") {
          toast.error({ message: response.message });
        }
      } catch (error) {
        setDisableConfirmationButton(false);
        console.error(error);
        toast.error({ message: "Could not cancel booking " });
      } finally {
        await invalidateOrderCache(orderID);
        await invalidateOrderLineItemCache(orderItemID);
        setTimeout(() => {
          setViewCancelOrderConfirmation(false);
        }, 300);
      }
    },
    [cancelOrder, toast, orderItemID]
  );

  return (
    <Container>
      <Helmet>
        <title>
          {offeringName} - {partnerOffering.partner.name}
        </title>
        <link href={favicon} rel="shortcut icon" type="image/x-icon" />
      </Helmet>
      {/* NAVBAR */}
      {partnerConfig.slug === offeringConfig.WeWork.partnerSlug ? (
        <OfferingHeader
          partnerID={partnerOffering.partner.id}
          offeringID={partnerOfferingID}
          homepageURL={partnerOffering.partner.homepageUrl}
          logo={partnerOffering.partner.logoUrl}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
          hubspotForm={hubspotForm}
          localeOptions={localeOptions}
          showLanguageSelector={!!partnerConfig.showLanguageSelector}
        />
      ) : (
        <OfferingHeaderV2
          partnerID={partnerOffering.partner.id}
          offeringID={partnerOfferingID}
          homepageURL={partnerOffering.partner.homepageUrl}
          logo={partnerOffering.partner.logoUrl}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
          talkWithUsMessage={talkWithUsMessage}
          hubspotForm={hubspotForm}
          localeOptions={localeOptions}
          hubspotForms={hubspotForms}
        />
      )}
      <Spacer size={24} />
      <div className="container">
        <div className="row">
          {/* DESKTOP LEFT SIDE */}
          <div className="col-12 col-md-7 col-xl-8">
            <div>
              {renderOrderTitle()}
              {space.productType?.invites &&
                numberOfInvites &&
                !orderItemAddon && (
                  <Fragment>
                    <Spacer size={16} />
                    <SendInvites
                      numberOfInvites={numberOfInvites || 5}
                      existingInvites={invites}
                      refetchInvites={refetchInvites}
                      order={order}
                      offeringName={location.name}
                    />
                    <Spacer size={16} />
                  </Fragment>
                )}
              <Spacer size={24} />
              <Row>
                <Col xs={12} lg={4}>
                  <OrderPaymentMethod order={order} />
                  <Spacer size={24} />
                  <OrderGuestDetails order={order} />
                  <Spacer size={24} />
                </Col>
                <Col lg={8}>
                  {order.bookingReason && (
                    <Fragment>
                      <Text color="muted" size="sm">
                        {fbt("bookingReason")}
                      </Text>
                      <Spacer size={8} />
                      <Text>{order.bookingReason}</Text>
                      <Spacer size={24} />
                    </Fragment>
                  )}
                  {space.bookingPolicy.requireTaxId &&
                    !!order.billingDetails?.tax && (
                      <Fragment>
                        <OrderTax order={order} />
                        <Spacer size={24} />
                      </Fragment>
                    )}
                  <OrderBillingDetails order={order} />
                  <Spacer size={24} />
                </Col>
              </Row>
              {order.specialInstructions && (
                <div>
                  <Text color="muted" size="sm">
                    {fbt("Special instructions")}
                  </Text>
                  <Spacer size={8} />
                  <Text>{order.specialInstructions}</Text>
                  <Spacer size={24} />
                </div>
              )}
              {/* Promobox */}
              <PromoCodeBox
                spaceID={space?.id}
                locale={locale}
                partnerOffering={partnerOffering}
              />
            </div>
          </div>
          {/* DESKTOP RIGHT SIDE */}
          <div className="col-12 col-md-5 col-xl-4 mt-5 mt-md-0">
            <Container>
              <OrderSummary
                orderItem={mainOrderItem}
                location={location}
                order={order}
                space={space}
                currency={space.currencyCode}
                offeringName={offeringName}
              />

              {order.cancellable && (
                <div className="text-end px-3">
                  <Button
                    variant="link"
                    className="text-dark"
                    onClick={() => handleOpenDialog()}
                  >
                    {fbt("Cancel Order")}
                  </Button>
                  <Modal
                    className="header-center"
                    show={viewCancelOrderConfirmation}
                    fullscreen="sm-down"
                    onHide={() => setViewCancelOrderConfirmation(false)}
                  >
                    <Modal.Header closeButton className="cancle-order__header">
                      <Modal.Title className="fw-bold">
                        {fbt("Cancellation")}
                      </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <p>
                        {fbt(
                          "Are you sure you want to cancel and get a refund? This cannot be un-done."
                        )}
                      </p>
                      <div className="d-flex justify-content-center justify-content-md-end flex-wrap-reverse">
                        <Button
                          variant="link"
                          className="text-dark"
                          disabled={disableConfirmationButton}
                          onClick={() => setViewCancelOrderConfirmation(false)}
                        >
                          {fbt("No, do not cancel")}
                        </Button>
                        <Button
                          variant="primary"
                          disabled={disableConfirmationButton}
                          onClick={() => handleCancelOrder(order.id)}
                        >
                          {fbt("Yes, Cancel & Refund")}
                        </Button>
                      </div>
                    </Modal.Body>
                  </Modal>
                </div>
              )}
            </Container>
          </div>
        </div>

        {/* TALK TO US MOBILE */}
        <div className="py-1 d-flex justify-content-center fw-bold d-block d-md-none mt-4">
          {showTalkWithUsPrompt ? (
            <TalkWithUsPrompt
              partnerID={partnerOffering.partner.id}
              hubspotForm={hubspotForm}
              messageableID={partnerOffering.id}
              hubspotForms={hubspotForms}
            />
          ) : (
            talkWithUsMessage && `${talkWithUsMessage}`
          )}
        </div>

        <div className="py-4">
          {/* FOOTER */}
          <Spacer size={40} />
          <FooterV2 instagramLink={instagramLink} />
        </div>
      </div>
    </Container>
  );
}

interface TemplateMembershipOfferingConfirmOrderQueryParams {
  locationID?: string;
  fromCatalog?: string;
  promoCode?: string;
  subscriptionStartDate?: string;
}
interface TemplateMembershipOfferingConfirmOrderBaseProps {
  partnerConfig: PartnerConfig;
  confirmationMessage?: (
    order: Order,
    orderItem: OrderItem | undefined
  ) => React.ReactNode;
  location?: Location;
  offeringName: string;
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  order: Order;
  includeQuantity?: boolean;
  numberOfInvites?: number;
  locatableType?: LocatableType;
}

const intelligentOfficeRichardPromoOfferingIds = [
  "e8159490-e2ec-40c7-aa27-de6f9e1b7534",
  "500fcd70-a647-40e7-a942-659a8d8be3b1",
  "1eb15610-544d-4188-bdd7-a583450ece12",
];

export function TemplateMembershipOfferingConfirmOrderBaseV2(
  props: TemplateMembershipOfferingConfirmOrderBaseProps
) {
  const {
    order,
    location,
    offeringName,
    partnerOffering,
    partnerOfferingID,
    partnerConfig,
    confirmationMessage,
    locatableType,
  } = props;
  const {
    hubspotForm,
    favicon,
    showTalkWithUsPrompt = true,
    talkWithUsMessage,
    localeOptions,
    instagramLink,
    hubspotForms,
  } = partnerConfig;
  const { locale, fbt } = useI18n();
  const mq = useMediaQuery();
  const intelligentOfficePromoFeatureFlag =
    useIntelligentOfficePromoFeatureFlag(partnerOffering.partner.id);
  const [viewCancelOrderConfirmation, setViewCancelOrderConfirmation] =
    useState<boolean>(false);
  const [disableConfirmationButton, setDisableConfirmationButton] =
    useState<boolean>(false);

  const queryParams =
    useQueryString<TemplateMembershipOfferingConfirmOrderQueryParams>();

  const selectedMembership = useMemo(
    () =>
      getMembershipInfoFromLocation(
        partnerOffering.offeringLocations,
        location?.id
      ),
    [location, partnerOffering.offeringLocations]
  );

  const selectedOfferingLocation = useMemo(
    () =>
      getMembershipOfferingLocationFromLocationId(
        partnerOffering.offeringLocations,
        location?.id
      ),
    [location, partnerOffering.offeringLocations]
  );

  const { data: membershipPolicy } = useMembershipPoliciesQuery(
    selectedOfferingLocation?.id
  );

  const cancelOrder = useCancelOrder();
  const toast = useToast();

  const mainOrderItem = getMainOrderItem(order);

  const handleOpenDialog = useCallback(() => {
    setViewCancelOrderConfirmation(true);
  }, []);

  const orderItemBooking = order.orderItems.find(
    (orderItem) => orderItem.type === OrderableType.Booking
  );

  const orderItemAddon = order.orderItems.find(
    (orderItem) => orderItem.type === OrderableType.AddOn
  ) as OrderItemMultiPass;

  const orderItemID = orderItemBooking?.orderItemID
    ? orderItemBooking?.orderItemID
    : orderItemAddon?.orderItemID;

  const cancelledAt = order.cancelledAt
    ? fbt.formatDateByPattern(parseISO(order.cancelledAt), " MMM do, yyyy")
    : "";

  const handleCancelOrder = useCallback(
    async (orderID: string) => {
      try {
        setDisableConfirmationButton(true);
        const response = await cancelOrder(orderID, CANCEL_REASON);
        if (response.status === "failed") {
          toast.error({ message: response.message });
        }
      } catch (error) {
        setDisableConfirmationButton(false);
        console.error(error);
        toast.error({ message: "Could not cancel booking " });
      } finally {
        await invalidateOrderCache(orderID);
        await invalidateOrderLineItemCache(orderItemID);
        setTimeout(() => {
          setViewCancelOrderConfirmation(false);
        }, 300);
      }
    },
    [cancelOrder, toast, orderItemID]
  );

  const purchaseStatus = useCheckPurchaseStatus({
    email: order.email,
    partnerId: partnerOffering.partner.id,
  });

  return (
    <Container>
      <Helmet>
        <title>
          {offeringName} - {partnerOffering.partner.name}
        </title>
        <link href={favicon} rel="shortcut icon" type="image/x-icon" />
      </Helmet>
      {/* NAVBAR */}
      <OfferingHeaderV2
        partnerID={partnerOffering.partner.id}
        offeringID={partnerOfferingID}
        homepageURL={partnerOffering.partner.homepageUrl}
        logo={partnerOffering.partner.logoUrl}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
        talkWithUsMessage={talkWithUsMessage}
        hubspotForm={hubspotForm}
        localeOptions={localeOptions}
      />
      <Spacer size={mq.device === "mobile" ? 24 : 48} />
      <div className="container">
        <div className="row">
          {/* DESKTOP LEFT SIDE */}
          <div className="col-12 col-md-6 col-xl-7">
            <div>
              {order.status === OrderStatus.Canceled ? (
                <Fragment>
                  <h1>{fbt("Order cancelled")}</h1>
                  <Spacer size={9} />
                  <p>
                    <b>{fbt("Cancelled and refunded ")}</b>
                    {fbt("on " + fbt.param("cancelledAt", cancelledAt) + ".")}
                  </p>
                </Fragment>
              ) : order.status === OrderStatus.DisputePending ? (
                <Fragment>
                  <h1>{fbt("Order is being disputed")}</h1>
                  <Spacer size={9} />
                </Fragment>
              ) : order.status === OrderStatus.DisputeLost ? (
                <Fragment>
                  <h1>{fbt("Order was disputed")}</h1>
                  <Spacer size={9} />
                </Fragment>
              ) : (
                <Fragment>
                  <h1>
                    {fbt(
                      "Welcome to " +
                        fbt.param("partnerName", partnerOffering.partner.name) +
                        "!"
                    )}
                  </h1>
                  <Spacer size={mq.device === "mobile" ? 24 : 40} />
                  {confirmationMessage ? (
                    confirmationMessage(order, mainOrderItem)
                  ) : (
                    <div>
                      <h3>{fbt("Your membership is confirmed")}</h3>
                      <Spacer size={mq.device === "mobile" ? 8 : 16} />
                      <p>
                        {fbt(
                          "You are now a " +
                            fbt.param(
                              "partnerName",
                              partnerOffering.partner.name
                            ) +
                            " member." +
                            " A confirmation email has been sent to " +
                            fbt.param("email", order.email) +
                            " with further instructions to activate your membership. We are looking forward to seeing you soon."
                        )}
                      </p>
                    </div>
                  )}
                  <PortalOnboarding
                    email={order.email}
                    purchaseStatus={purchaseStatus}
                    partnerId={partnerOffering.partner.id}
                    partnerSlug={partnerOffering.partner.partnerSlug}
                  />
                </Fragment>
              )}

              <Spacer size={mq.device === "mobile" ? 12 : 24} />
              <h3>{fbt("Customer information")}</h3>
              <Spacer size={mq.device === "mobile" ? 0 : 24} />
              <Row>
                <Col xs={12} lg={4}>
                  <OrderGuestDetailsMembership order={order} />
                  <Spacer size={24} />
                </Col>

                <Col lg={8}>
                  {order.bookingReason && (
                    <Fragment>
                      <Text color="muted" size="sm">
                        {fbt("bookingReason")}
                      </Text>
                      <Spacer size={8} />
                      <Text>{order.bookingReason}</Text>
                      <Spacer size={24} />
                    </Fragment>
                  )}

                  <OrderPaymentMethodMembership order={order} />
                  <Spacer size={24} />
                  <OrderBillingDetailsMembership order={order} />
                  <Spacer size={24} />
                </Col>
              </Row>
              {membershipPolicy?.cancellationText && (
                <Container flex={1}>
                  <CancellationPolicy
                    html={membershipPolicy?.cancellationText}
                    variant="gray"
                  />
                </Container>
              )}
              {/* Promobox */}
              <PromoCodeBox
                spaceID={undefined}
                locale={locale}
                partnerOffering={partnerOffering}
                locationID={selectedMembership?.locatable?.id}
              />
              {intelligentOfficePromoFeatureFlag &&
                intelligentOfficeRichardPromoOfferingIds.includes(
                  partnerOfferingID
                ) && <IntelligentOfficeRichardPromo />}
            </div>
          </div>
          {/* DESKTOP RIGHT SIDE */}
          <div className="col-12 col-md-5 offset-md-1 col-xl-4">
            <Container>
              <MembershipOrderSummary
                location={location}
                order={order}
                offeringName={offeringName}
                selectedMembership={selectedMembership}
                subscriptionStartDate={queryParams.subscriptionStartDate}
              />

              {order.cancellable && !locatableType && (
                <div className="text-end px-3">
                  <Button
                    variant="link"
                    className="text-dark"
                    onClick={() => handleOpenDialog()}
                  >
                    {fbt("Cancel Order")}
                  </Button>
                  <Modal
                    className="header-center"
                    show={viewCancelOrderConfirmation}
                    fullscreen="sm-down"
                    onHide={() => setViewCancelOrderConfirmation(false)}
                  >
                    <Modal.Header closeButton className="cancle-order__header">
                      <Modal.Title className="fw-bold">
                        {fbt("Cancellation")}
                      </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <p>
                        {fbt(
                          "Are you sure you want to cancel and get a refund? This cannot be un-done."
                        )}
                      </p>
                      <div className="d-flex justify-content-center justify-content-md-end flex-wrap-reverse">
                        <Button
                          variant="link"
                          className="text-dark"
                          disabled={disableConfirmationButton}
                          onClick={() => setViewCancelOrderConfirmation(false)}
                        >
                          {fbt("No, do not cancel")}
                        </Button>
                        <Button
                          variant="primary"
                          disabled={disableConfirmationButton}
                          onClick={() => handleCancelOrder(order.id)}
                        >
                          {fbt("Yes, Cancel & Refund")}
                        </Button>
                      </div>
                    </Modal.Body>
                  </Modal>
                </div>
              )}
            </Container>
          </div>
        </div>
        <Spacer size={mq.device === "mobile" ? 36 : 0} />
        {/* TALK TO US MOBILE */}
        <div className="py-1 d-flex justify-content-center fw-bold d-block d-md-none mt-4">
          {showTalkWithUsPrompt ? (
            <TalkWithUsPrompt
              partnerID={partnerOffering.partner.id}
              hubspotForm={hubspotForm}
              messageableID={partnerOffering.id}
              hubspotForms={hubspotForms}
            />
          ) : (
            talkWithUsMessage && `${talkWithUsMessage}`
          )}
        </div>

        <div className={classNames({ "py-4": mq.device !== "mobile" })}>
          {/* FOOTER */}
          <Spacer size={mq.device === "mobile" ? 30 : 40} />
          <FooterV2 instagramLink={instagramLink} />
        </div>
      </div>
    </Container>
  );
}
